import { Ref, useCallback, useEffect, useRef, useState } from 'react';
import { api, Attempt, AttemptSection, Question, ResponseState } from '../api';
import { useAnnounce } from '../a11y/announce';
import { Icon } from '../Icon';

function fmt(seconds: number) {
  const m = Math.floor(seconds / 60), s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function Delivery({ attempt: initial, onSubmitted }: {
  attempt: Attempt; onSubmitted: (attemptId: string) => void;
}) {
  const [attempt, setAttempt] = useState(initial);
  const [questions, setQuestions] = useState<Question[]>([]);
  // A resumed attempt opens on the first question still unanswered in the
  // open section, not back at the start of the paper.
  const [index, setIndex] = useState(() => {
    const open = initial.sections?.find((s) => s.state === 'open');
    const from = open?.firstIndex ?? 0, to = open?.lastIndex ?? initial.questionOrder.length - 1;
    const answered = new Set(initial.responses
      .filter((r) => r.selectedResponse?.length).map((r) => r.questionId));
    for (let i = from; i <= to; i++) if (!answered.has(initial.questionOrder[i]!)) return i;
    return from;
  });
  const [responses, setResponses] = useState<Map<string, ResponseState>>(
    () => new Map(initial.responses.map((r) => [r.questionId, r])));
  const [sections, setSections] = useState<AttemptSection[] | null>(initial.sections);
  const [advancing, setAdvancing] = useState(false);
  // Reporting a suspect answer key. Kept per-question so moving away and back
  // does not lose a half-written report.
  const [reportOpen, setReportOpen] = useState(false);
  const [reportChoice, setReportChoice] = useState('');
  const [reportNote, setReportNote] = useState('');
  const [reported, setReported] = useState<Set<string>>(new Set());
  const [reportState, setReportState] = useState<'idle' | 'sending' | 'error'>('idle');
  const [reportError, setReportError] = useState<string | null>(null);
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  /**
   * An option the learner has picked but not yet submitted, per question.
   *
   * Answers are final, so choosing is two steps: pick, then confirm. A stray
   * tap on a phone — or an arrow key, which moves a radio group's selection
   * by design — must never be the thing that locks an answer in.
   */
  const [pending, setPending] = useState<Map<string, string>>(new Map());
  const [confirming, setConfirming] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [expired, setExpired] = useState(false);
  /**
   * The end-of-section review screen, as in the real exam: before a section
   * closes the learner sees every question in it, flagged and unanswered ones
   * easy to find, and can go back to any of them. Only in a revisable attempt —
   * where answers can still change, so a second look can still matter.
   */
  const [reviewing, setReviewing] = useState(false);
  const [reviewFilter, setReviewFilter] = useState<'all' | 'flagged' | 'unanswered'>('all');
  const [fromReview, setFromReview] = useState(false);
  /**
   * Revisable saves, one in flight per question. Arrow keys move a radio
   * group's selection — and fire a change — on every option they pass, so
   * firing a save per change would race; the one that landed last could be an
   * option the learner only passed through. Instead the newest choice waits
   * and is sent when the current save returns. `desired` is what the radios
   * show meanwhile; the server's answer replaces it once the queue drains.
   */
  const [desired, setDesired] = useState<Map<string, string>>(new Map());
  const queueRef = useRef(new Map<string, string>());
  const busyRef = useRef(new Set<string>());
  const revRef = useRef(new Map<string, number>());

  const announce = useAnnounce();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const submitBtnRef = useRef<HTMLButtonElement>(null);

  const milestones: number[] = attempt.settings?.timerMilestones ?? [600, 300, 60];
  // Fixed by the server's snapshot when the attempt started; the client only
  // reads it. The server enforces it regardless.
  const revisable = Boolean(attempt.settings?.answersRevisable);

  // A sectioned attempt has as many clocks as it has sections, and only the
  // open one governs. An unsectioned attempt keeps the single attempt clock.
  const activeSection = sections?.find((s) => s.state === 'open') ?? null;
  const pendingSection = sections?.find((s) => s.state === 'not_started') ?? null;
  const onBreak = Boolean(sections && !activeSection && pendingSection);
  const lastSection = sections ? sections[sections.length - 1]! : null;

  const [remaining, setRemaining] = useState<number | null>(
    initial.sections?.find((s) => s.state === 'open')?.remainingSeconds ?? initial.remainingSeconds);

  // Navigation is bounded by the open section: questions outside it are either
  // closed or not yet open, so offering them would be offering a dead end.
  const lo = activeSection?.firstIndex ?? 0;
  const hi = activeSection?.lastIndex ?? attempt.questionOrder.length - 1;

  const current = questions[index];
  const visibleIds = attempt.questionOrder.slice(lo, hi + 1);
  const answeredInSection = visibleIds
    .filter((qid) => responses.get(qid)?.selectedResponse?.length).length;
  const answeredCount = [...responses.values()].filter((r) => r.selectedResponse?.length).length;

  useEffect(() => { api.get<Question[]>(`/attempts/${attempt.id}/questions`).then(setQuestions); },
    [attempt.id]);

  // The countdown shown here is cosmetic. Every poll replaces it with the
  // server's figure, and the server refuses late writes regardless of what this
  // number says (FR-043).
  useEffect(() => {
    if (remaining === null) return;
    const tick = setInterval(() => setRemaining((r) => (r === null ? null : Math.max(0, r - 1))), 1000);
    const poll = setInterval(async () => {
      try {
        const fresh = await api.get<Attempt>(`/attempts/${attempt.id}`);
        setSections(fresh.sections);
        const open = fresh.sections?.find((s) => s.state === 'open') ?? null;
        setRemaining(fresh.sections ? (open?.remainingSeconds ?? null) : fresh.remainingSeconds);
        if (fresh.state !== 'in_progress') { setExpired(true); onSubmitted(attempt.id); }
      } catch { /* transient: the local countdown keeps the UI honest enough */ }
    }, 15000);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, [attempt.id, remaining === null, onSubmitted]);

  useEffect(() => {
    if (remaining === null) return;
    if (remaining === 0) {
      // A section running out closes that section; only the last one ends the
      // attempt. The server has already stopped accepting answers either way.
      if (activeSection && pendingSection) { void endSection(false); return; }
      if (!expired) { setExpired(true); void doSubmit(); }
      return;
    }
    const hit = milestones.find((m) => remaining === m);
    if (hit) {
      announce.timeMilestone(activeSection
        ? `${fmt(hit)} remaining in section ${activeSection.sectionIndex + 1}.`
        : `${fmt(hit)} remaining.`);
    }
  }, [remaining]);

  // Entering a section puts the learner on its first question rather than
  // wherever the previous one left the cursor.
  // A review belongs to the section it was opened for; when that section
  // closes (by the learner or its clock) the review closes with it.
  useEffect(() => {
    if (activeSection) setIndex((i) => (i < lo || i > hi ? lo : i));
    setReviewing(false); setFromReview(false);
  }, [activeSection?.sectionIndex]);

  // Focus moves to the question heading on navigation, so a screen-reader user
  // lands on the new question instead of being dropped at the document top.
  // Coming back to the review, focus returns to the row the learner opened, so
  // they carry on down the list rather than starting again from the top.
  useEffect(() => {
    const row = reviewing && fromReview ? document.getElementById(`review-q-${index}`) : null;
    (row ?? headingRef.current)?.focus();
  }, [index, reviewing]);

  // A new question means a new report; carrying the previous one over would
  // invite filing it against the wrong item.
  useEffect(() => {
    setReportOpen(false); setReportChoice(''); setReportNote('');
    setReportState('idle'); setReportError(null);
  }, [index]);

  /**
   * Report that this question's answer key looks wrong.
   *
   * Allowed mid-attempt, because the moment someone notices is while they are
   * reading the question. It does not change their answer or their score: a
   * genuine error is corrected by publishing a new bank version.
   */
  async function submitReport(questionId: string) {
    if (!reportChoice) { setReportError('Choose the option you believe is correct.'); return; }
    setReportState('sending'); setReportError(null);
    try {
      await api.post(`/attempts/${attempt.id}/disputes/${questionId}`, {
        proposedResponse: [reportChoice], comment: reportNote.trim() || undefined,
      });
      setReported((s) => new Set(s).add(questionId));
      setReportOpen(false); setReportState('idle');
      announce.saveState('Report sent. An editor will review it.');
    } catch (e: any) {
      setReportState('error');
      setReportError(e.status === 409
        ? 'You have already reported this question in this assessment.'
        : e.message);
    }
  }

  /**
   * Record an answer. ANSWERS ARE FINAL — the server refuses a second one, and
   * the UI disables the choices, so the learner is never offered an action that
   * will be rejected.
   */
  // The revision a change builds on, kept in a ref because a queued save runs
  // after the render that scheduled it.
  useEffect(() => {
    for (const r of responses.values()) {
      if ((revRef.current.get(r.questionId) ?? 0) < r.revisionNumber) {
        revRef.current.set(r.questionId, r.revisionNumber);
      }
    }
  }, [responses]);

  const answer = useCallback(async (questionId: string, choice: string) => {
    if (responses.get(questionId)?.locked) return;
    setConfirming(true);
    setSaving('saving');
    try {
      const saved = await api.put<ResponseState>(`/attempts/${attempt.id}/responses/${questionId}`, {
        // Stable token per intent: a retry after a timeout is a no-op server
        // side rather than a rejected "change" (FR-042). In a revisable
        // attempt the intent is "change revision N to this choice", so going
        // A → B → A is three distinct saves rather than the last being
        // swallowed as a retry of the first.
        clientToken: revisable
          ? `${questionId}:r${revRef.current.get(questionId) ?? 0}:${choice}`
          : `${questionId}:ans:${choice}`,
        selectedResponse: [choice],
      });
      // Render what the SERVER stored, never the optimistic local value.
      setResponses((m) => new Map(m).set(questionId, saved));
      revRef.current.set(questionId, saved.revisionNumber);
      setSaving('saved');
      // Announced once the learner has settled, not for every option an arrow
      // key passed over on the way.
      if (!revisable) announce.saveState('Answer recorded. Answers are final and cannot be changed.');
      else if (!queueRef.current.has(questionId)) {
        // Name what the SERVER stored: a retried token returns the current
        // answer, which need not be the option just clicked.
        const stored = saved.selectedResponse?.[0] ?? choice;
        announce.saveState(`Answer ${stored.toUpperCase()} saved. You can change it until this section ends.`);
      }
    } catch (e: any) {
      setSaving('error');
      if (e.status === 410) {
        setExpired(true);
        announce.saveState('Time has expired. Your saved answers were submitted.');
      } else if (e.status === 409) {
        // Answered or changed elsewhere (another tab). Take the server's word for it.
        try {
          const fresh = await api.get<Attempt>(`/attempts/${attempt.id}`);
          setResponses(new Map(fresh.responses.map((r) => [r.questionId, r])));
        } catch { /* leave local state as-is */ }
        announce.saveState(revisable
          ? 'This answer was changed elsewhere at the same time. Check the answer shown.'
          : 'That question was already answered. Answers are final.');
      } else {
        announce.saveState('Could not save. Your answer is not recorded yet.');
      }
    } finally {
      setConfirming(false);
    }
    // Recorded or refused, the pick is no longer pending: on success the
    // server's answer shows; on failure the learner picks again deliberately.
    setPending((m) => { const n = new Map(m); n.delete(questionId); return n; });
  }, [attempt.id, responses, announce, revisable]);

  /** Revisable answering: show the choice now, save it when the line is free. */
  async function choose(questionId: string, choice: string) {
    setDesired((m) => new Map(m).set(questionId, choice));
    queueRef.current.set(questionId, choice);
    if (busyRef.current.has(questionId)) return;
    busyRef.current.add(questionId);
    try {
      while (queueRef.current.has(questionId)) {
        const next = queueRef.current.get(questionId)!;
        queueRef.current.delete(questionId);
        await answer(questionId, next);
      }
    } finally {
      busyRef.current.delete(questionId);
      setDesired((m) => { const n = new Map(m); n.delete(questionId); return n; });
    }
  }

  function pick(questionId: string, choice: string) {
    setPending((m) => new Map(m).set(questionId, choice));
  }
  function clearPick(questionId: string) {
    setPending((m) => { const n = new Map(m); n.delete(questionId); return n; });
  }

  /** A flag is a bookmark, not an answer — it stays changeable throughout. */
  const toggleFlag = useCallback(async (questionId: string) => {
    const next = !responses.get(questionId)?.flagged;
    try {
      const saved = await api.put<ResponseState>(`/attempts/${attempt.id}/flags/${questionId}`, { flagged: next });
      setResponses((m) => new Map(m).set(questionId, saved));
      announce.saveState(next ? 'Flagged for review.' : 'Flag removed.');
    } catch {
      announce.saveState('Could not update the flag.');
    }
  }, [attempt.id, responses, announce]);

  async function doSubmit() {
    try { await api.post(`/attempts/${attempt.id}/submit`); } finally { onSubmitted(attempt.id); }
  }

  /**
   * Close the open section. `startNext` opens the following one immediately;
   * without it the learner lands on the break screen and starts when ready.
   *
   * Ending is irreversible server-side, so this is behind a confirmation
   * whenever the learner triggers it rather than the clock.
   */
  async function endSection(startNext: boolean) {
    if (advancing) return;
    setAdvancing(true);
    try {
      const res = await api.post<{ sections: AttemptSection[]; finished: boolean }>(
        `/attempts/${attempt.id}/sections/advance`, { startNext });
      setSections(res.sections);
      const open = res.sections.find((s) => s.state === 'open') ?? null;
      setRemaining(open?.remainingSeconds ?? null);
      if (res.finished) { setExpired(true); await doSubmit(); return; }
      announce.saveState(startNext
        ? `Section ${(open?.sectionIndex ?? 0) + 1} started.`
        : 'Section closed. Its questions are now final.');
    } catch {
      announce.saveState('Could not end the section. Your answers are saved.');
    } finally { setAdvancing(false); }
  }

  /** Open the confirmation dialog for ending the section or the attempt. */
  function openConfirm() { setConfirmOpen(true); dialogRef.current?.showModal(); }

  /**
   * In a revisable attempt, ending goes through the review screen first,
   * exactly as the real exam does. The review screen's own button then asks
   * for confirmation.
   */
  function openReview() {
    setReviewFilter('all'); setFromReview(false); setReviewing(true);
  }

  function requestSubmit() {
    if (revisable && !reviewing && (activeSection || !sections)) { openReview(); return; }
    const blanks = attempt.questionOrder.length - answeredCount;
    if (blanks > 0 || (remaining ?? 0) > 0) { setConfirmOpen(true); dialogRef.current?.showModal(); }
    else void doSubmit();
  }

  /** Ending a section is as final as submitting, so it asks the same way. */
  function requestEndSection() {
    if (revisable && !reviewing) { openReview(); return; }
    openConfirm();
  }

  // Between sections the learner is not in a question at all: showing one
  // would imply it is still answerable.
  if (onBreak && pendingSection && sections) {
    const done = sections.filter((s) => s.state === 'ended').length;
    return (
      <>
        <h1 tabIndex={-1} ref={headingRef} style={{ outline: 'none' }}>
          Section {done} complete
        </h1>
        <div className="card">
          <p>
            You have finished {done} of {sections.length} sections. Section {done} is now closed
            and its questions cannot be reopened.
          </p>
          <p>
            Section {pendingSection.sectionIndex + 1} holds {pendingSection.questionCount} questions
            with {Math.round(pendingSection.durationSeconds / 60)} minutes on its own clock. The
            clock starts when you begin, so take a break first if you want one.
          </p>
          <div className="btn-row">
            <button className="btn-primary" disabled={advancing}
                    onClick={() => void endSection(true)}>
              {advancing ? 'Starting…' : `Start section ${pendingSection.sectionIndex + 1}`}
            </button>
            <button disabled={advancing} onClick={requestSubmit}>Finish and submit now</button>
          </div>
        </div>

        <dialog ref={dialogRef} aria-labelledby="confirm-title"
                onClose={() => setConfirmOpen(false)}>
          <h2 id="confirm-title" style={{ marginTop: 0 }}>Submit without the remaining sections?</h2>
          <p>
            {sections.length - done} section{sections.length - done === 1 ? '' : 's'} remain
            unanswered and will be scored as blank.
          </p>
          <div className="btn-row">
            <button className="btn-primary" onClick={() => { dialogRef.current?.close(); void doSubmit(); }}>
              Submit now
            </button>
            <button onClick={() => dialogRef.current?.close()}>Go back</button>
          </div>
        </dialog>
      </>
    );
  }

  if (!current) return <p>Loading questions…</p>;

  const state = responses.get(current.id);
  const selected = state?.selectedResponse ?? [];
  const locked = Boolean(state?.locked);
  // A revisable attempt saves on choosing, so there is never a pending pick.
  const picked = !locked && !expired && !revisable ? pending.get(current.id) : undefined;
  const sectionLabel = activeSection ? `section ${activeSection.sectionIndex + 1}` : 'this exam';
  const flaggedInScope = visibleIds.filter((qid) => responses.get(qid)?.flagged).length;
  const scopeSize = hi - lo + 1;
  const answeredInScope = activeSection ? answeredInSection : answeredCount;
  const low = remaining !== null && remaining <= 60;

  /**
   * Whether this learner may see which option is correct, right now.
   *
   * Driven by the presence of the key in the payload rather than by reading
   * the policy ourselves: the server strips it for a timed mock, so if it is
   * here, revealing it is already sanctioned. Guessing from settings could
   * disagree with what was actually sent.
   */
  const revealed = Boolean(locked && current.correctResponse?.length);

  return (
    <>
      <div className="bar bar--sticky" style={{ marginBottom: '1.5rem' }}>
        <span>
          {activeSection && sections && (
            <>
              <strong>Section {activeSection.sectionIndex + 1}</strong>
              <span className="muted"> of {sections.length} · </span>
            </>
          )}
          Question <strong>{index - lo + 1}</strong> of {hi - lo + 1}
          <span className="muted"> · {activeSection ? answeredInSection : answeredCount} answered</span>
        </span>
        {remaining !== null && (
          <span className={`timer${low ? ' timer--low' : ''}`}>
            <span className="visually-hidden">Time remaining: </span>
            {fmt(remaining)}
          </span>
        )}
        <span className="muted" aria-hidden="true">
          {saving === 'saving' ? 'Saving…' : saving === 'saved' ? 'Saved' : saving === 'error' ? 'Not saved' : ''}
        </span>
      </div>

      {expired && (
        <div className="notice notice--warn" role="alert">
          Time expired. Your saved answers were submitted automatically.
        </div>
      )}

      {reviewing ? (
        <ReviewScreen
          headingRef={headingRef} endRef={submitBtnRef}
          title={activeSection ? `Review section ${activeSection.sectionIndex + 1}` : 'Review your answers'}
          ids={visibleIds} lo={lo} responses={responses}
          answered={answeredInScope} flagged={flaggedInScope} total={scopeSize}
          filter={reviewFilter}
          onFilter={(f) => {
            setReviewFilter(f);
            const n = f === 'all' ? scopeSize : f === 'flagged' ? flaggedInScope : scopeSize - answeredInScope;
            announce.saveState(`Showing ${n} ${f === 'all' ? '' : `${f} `}question${n === 1 ? '' : 's'}.`);
          }}
          endLabel={activeSection && pendingSection
            ? `End section ${activeSection.sectionIndex + 1}`
            : activeSection ? 'Finish exam' : 'Submit'}
          disabled={expired || advancing}
          onOpen={(i) => { setIndex(i); setReviewing(false); setFromReview(true); }}
          onBack={() => { setReviewing(false); setFromReview(false); }}
          onEnd={() => (activeSection && pendingSection ? openConfirm() : requestSubmit())} />
      ) : (<>
      <h1 ref={headingRef} tabIndex={-1} style={{ outline: 'none', marginBottom: '0.25rem' }}>
        Question {index + 1}
      </h1>
      {/* Topic sits on its own line rather than inside the heading: on a phone a
          long topic name wraps mid-heading and reads as a broken title. */}
      {attempt.mode === 'study' && current.topicNames.length > 0 && (
        <p className="muted" style={{ marginBottom: '1rem' }}>{current.topicNames.join(', ')}</p>
      )}

      <div className="card">
        <p style={{ fontSize: '1.05rem' }}>{current.stem}</p>

        {!locked && !expired && (
          <p className="muted" id={`final-note-${current.id}`}>
            {revisable
              ? `Choosing an option saves it. You can change it until ${sectionLabel} ends.`
              : 'Pick an option, then submit it. Once submitted, your answer is final and cannot be changed.'}
          </p>
        )}

        <fieldset style={{ border: 'none', padding: 0, margin: 0 }}
                  aria-describedby={!locked && !expired ? `final-note-${current.id}` : undefined}>
          <legend className="visually-hidden">Answer choices for question {index + 1}</legend>
          {current.options.map((o) => {
            // The key is only present when the server has decided this learner
            // may see it — study mode, or after submission. In a timed mock it
            // is absent from the payload entirely, so `revealed` is false and
            // nothing below can leak it.
            const isRight = revealed && current.correctResponse?.includes(o.key);
            const isWrongPick = revealed && selected.includes(o.key) && !isRight;
            return (
              <label key={o.key}
                     className={`choice${locked ? ' choice--locked' : ''}`
                       + (isRight ? ' choice--correct' : '')
                       + (isWrongPick ? ' choice--wrong' : '')}>
                <input type="radio" name={`q-${current.id}`} value={o.key}
                       checked={revisable
                         ? (desired.get(current.id) ?? selected[0]) === o.key
                         : locked ? selected.includes(o.key) : picked === o.key}
                       // A revisable save never disables the group: that
                       // would drop keyboard focus mid-arrow.
                       disabled={expired || locked || (confirming && !revisable)}
                       onChange={() => (revisable
                         ? void choose(current.id, o.key)
                         : pick(current.id, o.key))} />
                <span>
                  <span className="choice-key">{o.key.toUpperCase()}.</span>{o.text}
                  {/* Colour is never the only signal (FR-069): the state is
                      spelled out, so it survives greyscale, high contrast and
                      a screen reader. */}
                  {isRight && (
                    <strong className="choice-tag choice-tag--ok">
                      {' '}✓ Correct answer{selected.includes(o.key) ? ' — you chose this' : ''}
                    </strong>
                  )}
                  {isWrongPick && (
                    <strong className="choice-tag choice-tag--bad"> ✗ You chose this</strong>
                  )}
                </span>
              </label>
            );
          })}
        </fieldset>

        {picked && (
          <div className="answer-confirm" role="group" aria-labelledby={`confirm-q-${current.id}`}>
            <p id={`confirm-q-${current.id}`}>
              <strong>Submit answer {picked.toUpperCase()}?</strong>{' '}
              <span className="muted">Once submitted, it can't be changed.</span>
            </p>
            <div className="answer-confirm__actions">
              <button className="btn-primary" disabled={confirming}
                      onClick={() => void answer(current.id, picked)}>
                <Icon name="check-circle" />
                {confirming ? 'Submitting…' : `Submit answer ${picked.toUpperCase()}`}
              </button>
              <button className="btn-ghost" disabled={confirming} onClick={() => clearPick(current.id)}>
                Clear
              </button>
            </div>
          </div>
        )}

        {locked && (
          <p className="pill" style={{ marginTop: '0.75rem' }} role="status">
            🔒 Answer recorded — final
          </p>
        )}
        {revisable && selected.length > 0 && !expired && (
          <p className="pill" style={{ marginTop: '0.75rem' }}>
            ✓ Answer {selected[0]!.toUpperCase()} saved — you can change it until {sectionLabel} ends
          </p>
        )}

        <div className="btn-row" style={{ marginTop: '1rem' }}>
          <button onClick={() => toggleFlag(current.id)} disabled={expired}
                  aria-pressed={Boolean(state?.flagged)}>
            <Icon name="star" filled={Boolean(state?.flagged)} />
            {state?.flagged ? 'Flagged for review' : 'Flag for review'}
          </button>

          {/* Distinct from flagging. A flag is a private bookmark; this is a
              claim that the question itself is wrong, and it goes to an editor. */}
          {reported.has(current.id) ? (
            <span className="pill" role="status">✓ Reported — an editor will review it</span>
          ) : (
            <button onClick={() => setReportOpen((o) => !o)} disabled={expired}
                    aria-expanded={reportOpen} aria-controls={`report-${current.id}`}>
              <Icon name="flag" />Answer looks wrong
            </button>
          )}
        </div>

        {reportOpen && !reported.has(current.id) && (
          <div className="card card--plain" id={`report-${current.id}`} style={{ marginTop: '1rem' }}>
            <h2 style={{ marginTop: 0, fontSize: '1.05rem' }}>Report this question</h2>
            <p className="muted">
              Which option do you believe is the correct answer? This does not change your answer or
              your score — it is sent to an editor, and you earn a cookie if the report is accepted.
            </p>

            <fieldset style={{ border: 'none', padding: 0, margin: '0 0 0.75rem' }}>
              <legend className="visually-hidden">The option you believe is correct</legend>
              {current.options.map((o) => (
                <label key={o.key} className="choice">
                  <input type="radio" name={`report-choice-${current.id}`} value={o.key}
                         checked={reportChoice === o.key}
                         onChange={() => { setReportChoice(o.key); setReportError(null); }} />
                  <span><span className="choice-key">{o.key.toUpperCase()}.</span>{o.text}</span>
                </label>
              ))}
            </fieldset>

            <label htmlFor={`report-note-${current.id}`}>Why? <span className="muted">(optional)</span></label>
            <textarea id={`report-note-${current.id}`} rows={3} value={reportNote}
                      onChange={(e) => setReportNote(e.target.value)}
                      placeholder="A source or a short explanation helps an editor check it quickly."
                      style={{ width: '100%', font: 'inherit', padding: '0.5rem',
                               border: '1px solid var(--border-strong)', borderRadius: '8px',
                               background: 'var(--surface)', color: 'var(--ink)' }} />

            {reportError && (
              <div className="notice notice--warn" role="alert" style={{ marginTop: '0.75rem' }}>
                {reportError}
              </div>
            )}

            <div className="btn-row" style={{ marginTop: '0.75rem' }}>
              <button className="btn-primary" disabled={reportState === 'sending'}
                      onClick={() => void submitReport(current.id)}>
                {reportState === 'sending' ? 'Sending…' : <><Icon name="send" />Send report</>}
              </button>
              <button onClick={() => setReportOpen(false)}>Cancel</button>
            </div>
          </div>
        )}

        {revealed && selected.length > 0 && current.rationale && (
          <div className={`notice${current.correctResponse?.includes(selected[0]!) ? '' : ' notice--warn'}`}
               role="status" style={{ marginTop: '1rem' }}>
            <strong>
              {current.correctResponse?.includes(selected[0]!)
                ? '✓ Correct.'
                : `✗ Not correct — the answer is ${(current.correctResponse ?? []).join(', ').toUpperCase()}.`}
            </strong>{' '}
            {current.rationale}
            {current.sourceReference && <div className="muted" style={{ marginTop: '0.5rem' }}>Source: {current.sourceReference}</div>}
          </div>
        )}
      </div>

      <div className="btn-row btn-row--nav">
        {fromReview && (
          <button onClick={() => setReviewing(true)}>
            <Icon name="arrow-left" />Back to review
          </button>
        )}
        <button onClick={() => setIndex((i) => Math.max(lo, i - 1))} disabled={index <= lo}><Icon name="arrow-left" />Previous</button>
        <button onClick={() => setIndex((i) => Math.min(hi, i + 1))}
                disabled={index >= hi}>Next<Icon name="arrow-right" /></button>
        {activeSection && pendingSection ? (
          <button ref={submitBtnRef} className="btn-primary" onClick={requestEndSection}
                  disabled={expired || advancing}>
            End section {activeSection.sectionIndex + 1}
          </button>
        ) : (
          <button ref={submitBtnRef} className="btn-primary" onClick={requestSubmit} disabled={expired}>
            <Icon name="check-circle" />{activeSection ? 'Finish exam' : 'Submit session'}
          </button>
        )}
      </div>

      <h2 id="nav-heading">
        {activeSection ? `Section ${activeSection.sectionIndex + 1} navigator` : 'Question navigator'}
      </h2>
      <ul className="navigator" aria-labelledby="nav-heading">
        {visibleIds.map((qid, offset) => {
          const i = lo + offset;
          const r = responses.get(qid);
          const isAnswered = Boolean(r?.selectedResponse?.length);
          const isFlagged = Boolean(r?.flagged);
          const status = [isAnswered ? 'answered' : 'not answered', isFlagged ? 'flagged' : null]
            .filter(Boolean).join(', ');
          return (
            <li key={qid} className="nav-item">
              <button
                onClick={() => setIndex(i)}
                aria-current={i === index ? 'true' : undefined}
                className={`${isAnswered ? 'nav-answered' : ''} ${isFlagged ? 'nav-flagged' : ''}`}
                aria-label={`Question ${i - lo + 1}, ${status}`}>
                <span aria-hidden="true">{i - lo + 1}</span>
                {/* Status is carried by a glyph as well as colour (FR-069). */}
                <span className="glyph" aria-hidden="true">{isFlagged ? '★' : isAnswered ? '●' : '○'}</span>
              </button>
            </li>
          );
        })}
      </ul>
      <p className="muted">● answered · ○ not answered · ★ flagged</p>
      </>)}

      <dialog ref={dialogRef} aria-labelledby="confirm-title"
              onClose={() => { setConfirmOpen(false); submitBtnRef.current?.focus(); }}>
        {activeSection && pendingSection ? (
          <>
            <h2 id="confirm-title" style={{ marginTop: 0 }}>
              End section {activeSection.sectionIndex + 1}?
            </h2>
            <p>
              You have answered {answeredInSection} of {hi - lo + 1} questions in this section.
              {answeredInSection < hi - lo + 1 &&
                ` ${hi - lo + 1 - answeredInSection} will be scored as unanswered.`}
            </p>
            <p>
              <strong>This cannot be undone.</strong> Once the section closes
              {revisable ? ' its answers are final and' : ''} you cannot return to
              its questions, exactly as in the real examination.
            </p>
            {remaining !== null && remaining > 0 && (
              <p>You still have {fmt(remaining)} left on this section's clock.</p>
            )}
            <div className="btn-row">
              <button className="btn-primary" disabled={advancing}
                      onClick={() => { dialogRef.current?.close(); void endSection(false); }}>
                End section
              </button>
              <button onClick={() => dialogRef.current?.close()}>Keep working</button>
            </div>
          </>
        ) : (
          <>
            <h2 id="confirm-title" style={{ marginTop: 0 }}>
              {activeSection ? 'Finish this exam?' : 'Submit this session?'}
            </h2>
            <p>
              You have answered {answeredCount} of {attempt.questionOrder.length} questions.
              {answeredCount < attempt.questionOrder.length &&
                ` ${attempt.questionOrder.length - answeredCount} will be marked unanswered and still count toward your score.`}
            </p>
            {remaining !== null && remaining > 0 && <p>You still have {fmt(remaining)} remaining.</p>}
            <div className="btn-row">
              <button className="btn-primary" onClick={() => { dialogRef.current?.close(); void doSubmit(); }}>
                Submit now
              </button>
              <button onClick={() => dialogRef.current?.close()}>Keep working</button>
            </div>
          </>
        )}
      </dialog>
    </>
  );
}

/**
 * The end-of-section review screen.
 *
 * Every question in scope is listed with its status written out — never
 * colour alone (FR-069) — and the filters narrow it to the two lists that
 * matter before a section locks: flagged and unanswered. It shows which
 * option the learner chose, never whether it is right; the server has not sent
 * the key, so there is nothing here that could say.
 */
function ReviewScreen({
  headingRef, endRef, title, ids, lo, responses, answered, flagged, total,
  filter, onFilter, endLabel, disabled, onOpen, onBack, onEnd,
}: {
  headingRef: Ref<HTMLHeadingElement>; endRef: Ref<HTMLButtonElement>; title: string;
  ids: string[]; lo: number; responses: Map<string, ResponseState>;
  answered: number; flagged: number; total: number;
  filter: 'all' | 'flagged' | 'unanswered'; onFilter: (f: 'all' | 'flagged' | 'unanswered') => void;
  endLabel: string; disabled: boolean;
  onOpen: (index: number) => void; onBack: () => void; onEnd: () => void;
}) {
  const unanswered = total - answered;
  const rows = ids
    .map((qid, offset) => {
      const r = responses.get(qid);
      return { index: lo + offset, number: offset + 1,
               choice: r?.selectedResponse?.[0] ?? null, flagged: Boolean(r?.flagged) };
    })
    .filter((row) => filter === 'all'
      || (filter === 'flagged' && row.flagged)
      || (filter === 'unanswered' && !row.choice));

  const filters: { key: typeof filter; label: string }[] = [
    { key: 'all', label: `All (${total})` },
    { key: 'flagged', label: `Flagged (${flagged})` },
    { key: 'unanswered', label: `Unanswered (${unanswered})` },
  ];

  return (
    <>
      <h1 ref={headingRef} tabIndex={-1} style={{ outline: 'none' }}>{title}</h1>
      <div className="card">
        <p>
          <strong>{answered}</strong> of {total} answered · <strong>{unanswered}</strong> unanswered
          · <strong>{flagged}</strong> flagged.
        </p>
        <p className="muted">
          Open any question to check or change it, then come back here. Ending the section makes
          its answers final.
        </p>

        <div className="btn-row review-filters" role="group" aria-label="Show questions">
          {filters.map((f) => (
            <button key={f.key} aria-pressed={filter === f.key} onClick={() => onFilter(f.key)}
                    className={filter === f.key ? 'btn-primary' : undefined}>
              {f.label}
            </button>
          ))}
        </div>

        {rows.length === 0 ? (
          <p>
            {filter === 'flagged' ? 'No flagged questions.' : 'Every question has an answer.'}
          </p>
        ) : (
          <ul className="review-list" aria-label={`${rows.length} question${rows.length === 1 ? '' : 's'}`}>
            {rows.map((row) => (
              <li key={row.index}>
                <button id={`review-q-${row.index}`} onClick={() => onOpen(row.index)}>
                  <span className="review-list__q">Question {row.number}</span>
                  <span className={`review-list__status${row.choice ? '' : ' review-list__status--blank'}`}>
                    {row.choice ? `● Answered ${row.choice.toUpperCase()}` : '○ Not answered'}
                  </span>
                  {row.flagged && <span className="review-list__flag">★ Flagged</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="btn-row btn-row--nav">
        <button onClick={onBack}><Icon name="arrow-left" />Back to questions</button>
        <button ref={endRef} className="btn-primary" onClick={onEnd} disabled={disabled}>
          <Icon name="check-circle" />{endLabel}
        </button>
      </div>
    </>
  );
}
